/**
 * Movement Engine — how the eye travels through the design.
 *
 * A flow names the reading path (as waypoint roles, not coordinates — the
 * layout resolves geometry later), the visual endpoint where the CTA
 * belongs, and which Template Factory layout preset physically supports
 * the path. Energy gates the flows: a luxury flyer never gets a hard
 * diagonal, a festival flyer never gets a quiet F-pattern.
 */

export const FLOWS = {
  'z-shape': {
    id: 'z-shape', name: 'Z Shape',
    path: ['hero', 'primaryHeading', 'offer', 'cta'],
    endpoint: 'bottom-right', energyRange: [0.4, 0.8],
    factoryLayout: 'classic-flyer',
  },
  'f-shape': {
    id: 'f-shape', name: 'F Shape',
    path: ['primaryHeading', 'body', 'offer', 'cta'],
    endpoint: 'bottom-left', energyRange: [0.2, 0.6],
    factoryLayout: 'classic-flyer',
  },
  diagonal: {
    id: 'diagonal', name: 'Diagonal',
    path: ['offer', 'hero', 'primaryHeading', 'cta'],
    endpoint: 'bottom-right', energyRange: [0.6, 1],
    factoryLayout: 'offer-first',
  },
  'center-focus': {
    id: 'center-focus', name: 'Center Focus',
    path: ['hero', 'primaryHeading', 'offer', 'cta'],
    endpoint: 'center-bottom', energyRange: [0, 0.5],
    factoryLayout: 'centered-poster',
  },
  'left-focus': {
    id: 'left-focus', name: 'Left Focus',
    path: ['primaryHeading', 'body', 'cta', 'hero'],
    endpoint: 'left-column-bottom', energyRange: [0.2, 0.7],
    factoryLayout: 'split-hero',
  },
  'right-focus': {
    id: 'right-focus', name: 'Right Focus',
    path: ['hero', 'primaryHeading', 'body', 'cta'],
    endpoint: 'left-column-bottom', energyRange: [0.3, 0.7],
    factoryLayout: 'split-hero',
  },
  circular: {
    id: 'circular', name: 'Circular Flow',
    path: ['hero', 'primaryHeading', 'offer', 'cta', 'hero'],
    endpoint: 'center', energyRange: [0.3, 0.8],
    factoryLayout: 'centered-poster',
  },
};

export const listFlows = () =>
  Object.values(FLOWS).map(({ id, name }) => ({ id, name }));

export const getFlow = (id) => {
  const flow = FLOWS[id];
  if (!flow) throw new Error(`Unknown flow "${id}"`);
  return flow;
};

/**
 * Choose a flow for an energy level (0–1). Flows whose range contains the
 * energy are candidates; the RNG picks among them so equal briefs still
 * vary batch to batch (deterministically per seed).
 */
export const chooseFlow = (energy, rng) => {
  const candidates = Object.values(FLOWS).filter(
    ({ energyRange: [lo, hi] }) => energy >= lo && energy <= hi
  );
  const pool = candidates.length ? candidates : [FLOWS['z-shape']];
  return pool[Math.floor(rng() * pool.length)];
};
